import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { PAY_TYPE } from '../constants/payment';
import { useAppSelector } from '../store/hooks';
import { setFullAmount } from '../store/payment/slice';
import { costFormat, costFormatWithCurrency } from '../utils/cost-format';

const useAmount = () => {
  const { amount, paymentData, acquiring, fullAmount } = useAppSelector((state) => state.payment);
  const dispatch = useDispatch();

  const commission = useMemo(() => {
    if (Number(paymentData?.commission) > 0) {
      return paymentData?.commission;
    }

    if (Number(acquiring?.commission) > 0) {
      return acquiring?.commission;
    }

    return 0;
  }, [paymentData, acquiring]);

  useEffect(() => {
    const currentAmount = paymentData?.fix ? Number(paymentData?.amount) : Number(amount);
    let currentFullAmount = 0;

    if (paymentData?.paymentType === PAY_TYPE.PAYMENT) {
      currentFullAmount = (Math.abs(currentAmount) * 100 + Number(commission) * 100) / 100;

      dispatch(setFullAmount(costFormat(currentFullAmount).toString()));
    } else {
      currentFullAmount = (Math.abs(currentAmount) * 100 - Number(commission) * 100) / 100;

      dispatch(setFullAmount(costFormat(currentFullAmount).toString()));
    }
  }, [paymentData, amount, acquiring, commission, dispatch]);

  const formattedAmount = useMemo(() => {
    return costFormatWithCurrency(
      paymentData?.fix ? Math.abs(Number(paymentData?.amount)) : Number(amount),
      '₸',
    );
  }, [amount, paymentData]);

  const formattedFullAmount = useMemo(() => {
    return `${fullAmount} ₸`;
  }, [fullAmount]);

  /*
      Сумма без тиын
  */
  const currentAmount = useMemo(() => {
    return paymentData?.fix
      ? Math.abs(Number(paymentData?.amount)).toString()
      : Number(amount).toString();
  }, [amount, paymentData?.amount, paymentData?.fix]);

  // Сумма с тиын, используется для платежей
  const currentAmountForPayment = useMemo(() => {
    return (Number(currentAmount) * 100).toString();
  }, [currentAmount]);

  const formattedCommission = useMemo(() => {
    return `${commission} ₸`;
  }, [commission]);

  const isValidCurrentAmount = useMemo(() => {
    const amountNum = Number(currentAmount);
    const maxAmount = Number(paymentData?.maxAmount);
    const minAmount = Number(paymentData?.minAmount);
    const isNegative = maxAmount < 0 || minAmount < 0;
    const isValdCommission = amountNum > Number(commission);

    if (paymentData?.fix && isValdCommission) {
      return true;
    }

    if (!paymentData?.fix) {
      if (!isNegative) {
        return isValdCommission && amountNum <= maxAmount && amountNum > minAmount;
      }

      return isValdCommission;
    }

    return false;
  }, [currentAmount, commission, paymentData]);

  const formattedAmountWithCurrency = (currency?: string) => {
    return costFormatWithCurrency(
      paymentData?.fix
        ? Math.abs(Number(paymentData?.fullAmount || paymentData?.amount))
        : Number(amount),
      currency,
    );
  };

  const formattedFullAmountWithCurrency = (currency?: string) => {
    return costFormatWithCurrency(Number(fullAmount), currency);
  };

  return {
    commission,
    formattedCommission,
    currentAmount,
    currentAmountForPayment,
    formattedAmount,
    formattedFullAmount,
    isValidCurrentAmount,
    formattedAmountWithCurrency,
    formattedFullAmountWithCurrency,
  };
};

export { useAmount };
